import type { ChatProviderType, ChatIntegrationStatus } from "./types";

// ─── Provider Configuration ──────────────────────────────────────────────────

export const PROVIDER_CONFIG: Record<
  ChatProviderType,
  {
    label: string;
    emoji: string;
    available: boolean;
  }
> = {
  SLACK: { label: "Slack", emoji: "🔌", available: true },
  MS_TEAMS: { label: "Microsoft Teams", emoji: "💬", available: false },
  GOOGLE_CHAT: { label: "Google Chat", emoji: "🟢", available: false },
};

/** i18n key per provider description — resolve with t() at the render site. */
export const PROVIDER_DESCRIPTION_KEYS: Record<ChatProviderType, string> = {
  SLACK: "integrations.slack.connectDescription",
  MS_TEAMS: "integrations.provider.teamsDescription",
  GOOGLE_CHAT: "integrations.provider.googleChatDescription",
};

// ─── Status Display ──────────────────────────────────────────────────────────

/** i18n key per status — resolve with t() at the render site. */
export const STATUS_LABEL_KEYS: Record<ChatIntegrationStatus, string> = {
  PENDING_APPROVAL: "integrations.status.PENDING_APPROVAL",
  CONNECTED: "integrations.status.CONNECTED",
  DISCONNECTED: "integrations.status.DISCONNECTED",
  REVOKED: "integrations.status.REVOKED",
  ERROR: "integrations.status.ERROR",
};

export const STATUS_CONFIG: Record<
  ChatIntegrationStatus,
  { color: string; bg: string }
> = {
  PENDING_APPROVAL: {
    color: "text-yellow-800 dark:text-yellow-300",
    bg: "bg-yellow-100 dark:bg-yellow-900/30",
  },
  CONNECTED: {
    color: "text-green-800 dark:text-green-300",
    bg: "bg-green-100 dark:bg-green-900/30",
  },
  DISCONNECTED: {
    color: "text-muted-foreground",
    bg: "bg-muted",
  },
  REVOKED: {
    color: "text-red-800 dark:text-red-300",
    bg: "bg-red-100 dark:bg-red-900/30",
  },
  ERROR: {
    color: "text-red-800 dark:text-red-300",
    bg: "bg-red-100 dark:bg-red-900/30",
  },
};

// ─── OAuth Popup Config ──────────────────────────────────────────────────────

export const OAUTH_POPUP_OPTIONS = "width=600,height=700,scrollbars=yes";

// ─── Broadcast Setting Labels ────────────────────────────────────────────────

export const BROADCAST_SETTINGS = [
  {
    key: "challengeAnnouncementsEnabled" as const,
    labelKey: "integrations.broadcastSetting.challengeAnnouncements.label",
    descriptionKey: "integrations.broadcastSetting.challengeAnnouncements.description",
  },
  {
    key: "leaderboardDropsEnabled" as const,
    labelKey: "integrations.broadcastSetting.leaderboardDrops.label",
    descriptionKey: "integrations.broadcastSetting.leaderboardDrops.description",
  },
  {
    key: "rewardShoutoutsEnabled" as const,
    labelKey: "integrations.broadcastSetting.rewardShoutouts.label",
    descriptionKey: "integrations.broadcastSetting.rewardShoutouts.description",
  },
  {
    key: "eventAnnouncementsEnabled" as const,
    labelKey: "integrations.broadcastSetting.eventAnnouncements.label",
    descriptionKey: "integrations.broadcastSetting.eventAnnouncements.description",
  },
];

// ─── Test Broadcast Rate Limit ───────────────────────────────────────────────

export const TEST_BROADCAST_COOLDOWN_MS = 5 * 60 * 1000; // 5 minutes
